import moment from 'moment';
import { Button, Dropdown, Icon, InlineTooltip, Page, Tab, Table, Tabs, Tooltip } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { useApi, useSubscription, useToast, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors } from '~/styles';
import ClientMappings from './ClientMappings';
import ConfigurationDrawer from './ConfigurationDrawer';
import ExpenseCategoryMappings from './ExpenseCategoryMappings';
import InvoiceItemMappings from './InvoiceItemMappings';
import MemberMappings from './MemberMappings';
import DepositAccountMappings from './DepositAccountMappings';
import PaymentMethodMappings from './PaymentMethodMappings';
import SyncResults from './expenses/SyncResults';
import TaxRateMappings from './TaxRateMappings';
import WebhookDrawer from './WebhookDrawer';

const Loading = () => <Icon icon="spinner" spin />;

const OK = () => <Icon icon="check" color={colors.success} />;

const Error = ({ message = "Something's not right. Try reconnecting to your Sage Intacct company." }) => (
  <Tooltip message={message}>
    <Icon icon="exclamation-circle" color={colors.danger} />
  </Tooltip>
);

const Status = ({ status, message }) => {
  switch (status) {
    case 'ready':
      return <OK />;
    case 'error':
      return <Error message={message} />;
    default:
      return <Loading />;
  }
};

const Errors = styled.ul`
  font-size: 0.75rem;
  list-style: inside;

  li {
    list-style-type: disc;
  }
`;

const SyncResultsBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: ${colors.white};
  border: 1px solid ${colors.grey10};
  border-radius: 0.3125rem;
`;

export default function SageSettingsPage() {
  const { workspace } = useWorkspace();
  const api = useApi();
  const isMounted = useIsMounted();
  const toast = useToast();
  const location = useLocation();
  const { sageConnectionId } = useParams();

  const setup = new URLSearchParams(location.search).get('action') === 'setup';
  useEffect(() => {
    if (setup) window.history.pushState(null, null, window.location.href.split('?')[0]);
  }, [setup]);

  const { notify } = useSubscription();
  useEffect(() => {
    if (setup) notify(useSubscription.keys.integrations_changed);
  }, [setup, notify]);

  const [dialog, setDialog] = useState(null);
  const [isReauthorizing, setIsReauthorizing] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [forceRefreshSyncsDate, setForceRefreshSyncsDate] = useState(moment().format());
  const closeDialog = () => setDialog(null);

  const handleSyncNow = useCallback(async () => {
    try {
      await api.www.workspaces(workspace.id).sage(sageConnectionId).manualSync();
      toast.success('Reimbursable expenses sync has been queued.');
      setForceRefreshSyncsDate(moment().format());
    } catch (error) {
      toast.error(error.message);
    }
  }, [api, workspace.id, toast, sageConnectionId]);

  const handleReauthorize = async () => {
    setIsReauthorizing(true);
    try {
      await api.www.workspaces(workspace.id).sage(sageConnectionId).reauthorize();
      toast.success('Sage Intacct connection reauthorized.');
      await loadCompany();
    } catch ({ message }) {
      toast.error(message ?? 'Failed to reauthorize Sage Intacct.');
    } finally {
      if (isMounted.current) setIsReauthorizing(false);
    }
  };

  const [company, setCompany] = useState({ data: null, status: 'loading', message: null });

  const loadCompany = useCallback(async () => {
    setCompany((state) => ({ ...state, status: 'loading' }));
    try {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getStatus();
      if (!isMounted.current) return;
      if (data.status === 'error' || data.status === 'inactive') {
        setCompany({ data, status: 'error', message: data.message });
        return;
      }
      const { data: company } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getCompany();
      if (!isMounted.current) return;
      setCompany({ data: { ...data, name: company.name }, status: 'ready', message: data.message });
    } catch (error) {
      setCompany({ data: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const [configuration, setConfiguration] = useState({
    data: null,
    status: 'loading',
    message: null,
    syncMethod: null,
  });

  const loadConfiguration = useCallback(async () => {
    setConfiguration((state) => ({ ...state, status: 'loading' }));
    try {
      const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getConfigurationStatus();
      const { data: settings } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getConfiguration();
      if (!isMounted.current) return;
      if (data.code === 'ready')
        setConfiguration({ data, syncMethod: settings.syncMethod, status: 'ready', message: null });
      else
        setConfiguration({
          data,
          syncMethod: settings.syncMethod,
          status: 'error',
          message: 'Review the configuration settings.',
        });
    } catch (error) {
      setConfiguration({ data: null, syncMethod: null, status: 'error', message: error.message });
    }
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditConfiguration = () => setDialog('configuration');
  const handleCloseConfiguration = async () => {
    await loadConfiguration();
    closeDialog();
  };
  const setupConfiguration = useCallback(async () => {
    setConfiguration((state) => ({ ...state, status: 'loading' }));
    await api.www.workspaces(workspace.id).sage(sageConnectionId).setupConfiguration();
    toast.success('Loaded default configuration settings from Sage Intacct.');
    await loadConfiguration();
  }, [api, workspace.id, toast, loadConfiguration, sageConnectionId]);

  const [clients, setClients] = useState({ data: null, status: 'loading', message: null });

  const loadClients = useCallback(async () => {
    setClients((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getClientMappings();
    if (!isMounted.current) return;
    setClients({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditClients = () => setDialog('clients');
  const handleCloseClients = async () => {
    await loadClients();
    closeDialog();
  };
  const setupClients = useCallback(async () => {
    setClients((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupClients();
    toast.success(`Loaded ${data.length} Customers from Sage Intacct.`);
    await loadClients();
  }, [api, workspace.id, toast, loadClients, sageConnectionId]);

  const [invoiceItems, setInvoiceItems] = useState({ data: null, status: 'loading', message: null });

  const loadInvoiceItems = useCallback(async () => {
    setInvoiceItems((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getInvoiceItemMappings();
    if (!isMounted.current) return;
    setInvoiceItems({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditInvoiceItems = () => setDialog('invoice_items');
  const handleCloseInvoiceItems = async () => {
    await loadInvoiceItems();
    closeDialog();
  };
  const setupInvoiceItems = useCallback(async () => {
    setInvoiceItems((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupInvoiceItems();
    toast.success(`Loaded ${data.length} Items from Sage Intacct.`);
    await loadInvoiceItems();
  }, [api, workspace.id, toast, loadInvoiceItems, sageConnectionId]);

  const [taxRates, setTaxRates] = useState({ data: null, status: 'loading', message: null });

  const loadTaxRates = useCallback(async () => {
    setTaxRates((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getTaxRateMappings();
    if (!isMounted.current) return;
    setTaxRates({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditTaxRates = () => setDialog('tax_rates');
  const handleCloseTaxRates = async () => {
    await loadTaxRates();
    closeDialog();
  };
  const setupTaxRates = useCallback(async () => {
    setTaxRates((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupTaxRates();
    toast.success(`Loaded ${data.length} Tax Details from Sage Intacct.`);
    await loadTaxRates();
  }, [api, workspace.id, toast, loadTaxRates, sageConnectionId]);

  const [paymentMethods, setPaymentMethods] = useState({ data: null, status: 'loading', message: null });

  const loadPaymentMethods = useCallback(async () => {
    setPaymentMethods((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getPaymentMethodMappings();
    if (!isMounted.current) return;
    setPaymentMethods({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditPaymentMethods = () => setDialog('payment_methods');
  const handleClosePaymentMethods = async () => {
    await loadPaymentMethods();
    closeDialog();
  };
  const setupPaymentMethods = useCallback(async () => {
    setPaymentMethods((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupPaymentMethods();
    toast.success(`Loaded ${data.length} Payment Methods from Sage Intacct.`);
    await loadPaymentMethods();
  }, [api, workspace.id, toast, loadPaymentMethods, sageConnectionId]);

  const [depositAccounts, setDepositAccounts] = useState({ data: null, status: 'loading', message: null });

  const loadDepositAccounts = useCallback(async () => {
    setDepositAccounts((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getDepositAccountMappings();
    if (!isMounted.current) return;
    setDepositAccounts({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditDepositAccounts = () => setDialog('deposit_accounts');
  const handleCloseDepositAccounts = async () => {
    await loadDepositAccounts();
    closeDialog();
  };
  const setupDepositAccounts = useCallback(async () => {
    setDepositAccounts((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupDepositAccounts();
    toast.success(`Loaded ${data.length} Deposit Accounts from Sage Intacct.`);
    await loadDepositAccounts();
  }, [api, workspace.id, toast, loadDepositAccounts, sageConnectionId]);

  const [memberMappings, setMemberMappings] = useState({ data: null, status: 'loading', message: null });

  const loadMemberMappings = useCallback(async () => {
    setMemberMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getMemberMappings();
    if (!isMounted.current) return;
    setMemberMappings({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditMemberMappings = () => setDialog('member_mappings');
  const handleCloseMemberMappings = async () => {
    await loadMemberMappings();
    closeDialog();
  };
  const setupMemberMappings = useCallback(async () => {
    setMemberMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupMembers();
    toast.success(`Loaded ${data.length} Vendors from Sage Intacct.`);
    await loadMemberMappings();
  }, [api, workspace.id, toast, loadMemberMappings, sageConnectionId]);

  const handleEditWebhook = () => setDialog('webhook');
  const handleCloseWebhook = () => closeDialog();

  const [expenseCategoryMappings, setExpenseCategoryMappings] = useState({
    data: null,
    status: 'loading',
    message: null,
  });

  const loadExpenseCategoryMappings = useCallback(async () => {
    setExpenseCategoryMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).getExpenseCategoryMappings();
    if (!isMounted.current) return;
    setExpenseCategoryMappings({ data, status: 'ready', message: null });
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const handleEditExpenseCategoryMappings = () => setDialog('expense_category_mappings');
  const handleCloseExpenseCategoryMappings = async () => {
    await loadExpenseCategoryMappings();
    closeDialog();
  };
  const setupExpenseCategoryMappings = useCallback(async () => {
    setExpenseCategoryMappings((state) => ({ ...state, status: 'loading' }));
    const { data } = await api.www.workspaces(workspace.id).sage(sageConnectionId).setupExpenseCategories();
    toast.success(`Loaded ${data.length} Accounts from Sage Intacct.`);
    await loadExpenseCategoryMappings();
  }, [api, workspace.id, toast, loadExpenseCategoryMappings, sageConnectionId]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    if (setup) setupConfiguration();
    else loadConfiguration();
  }, [loadConfiguration, setupConfiguration, setup]);

  useEffect(() => {
    if (setup) setupClients();
    else loadClients();
  }, [loadClients, setupClients, setup]);

  useEffect(() => {
    if (setup) setupInvoiceItems();
    else loadInvoiceItems();
  }, [loadInvoiceItems, setupInvoiceItems, setup]);

  useEffect(() => {
    if (setup) setupTaxRates();
    else loadTaxRates();
  }, [loadTaxRates, setupTaxRates, setup]);

  useEffect(() => {
    if (setup) setupPaymentMethods();
    else loadPaymentMethods();
  }, [loadPaymentMethods, setupPaymentMethods, setup]);

  useEffect(() => {
    if (setup) setupDepositAccounts();
    else loadDepositAccounts();
  }, [loadDepositAccounts, setupDepositAccounts, setup]);

  useEffect(() => {
    if (setup) setupMemberMappings();
    else loadMemberMappings();
  }, [loadMemberMappings, setupMemberMappings, setup]);

  useEffect(() => {
    if (setup) setupExpenseCategoryMappings();
    else loadExpenseCategoryMappings();
  }, [loadExpenseCategoryMappings, setupExpenseCategoryMappings, setup]);

  const disabled = company.status !== 'ready';

  return (
    <>
      <Page.Tabs>
        <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
          <Tab>Settings</Tab>
          <Tab>Expense Sync</Tab>
        </Tabs>
      </Page.Tabs>

      {tabIndex === 0 && (
        <Page.Section>
          <Table>
            <Table.Header>
              <Table.Column width="12rem">Category</Table.Column>
              <Table.Column width="8rem" align="center">
                Status
              </Table.Column>
              <Table.Column>Details</Table.Column>
              <Table.BoxActionsColumn />
            </Table.Header>

            <Table.Body>
              <Table.BoxRow>
                <Table.Cell>
                  <strong>Company</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={company.status} message={company.message} />
                </Table.Cell>
                <Table.Cell>
                  {company.status === 'ready' && company.data?.name}

                  {company.status === 'error' && (
                    <Button isAnchor disabled={isReauthorizing} onClick={handleReauthorize}>
                      Fix Connection
                    </Button>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <Button disabled={isReauthorizing} onClick={handleReauthorize}>
                    <Icon icon={isReauthorizing ? 'spinner' : 'wrench'} spin={isReauthorizing} />
                    <InlineTooltip message="Reauthorize the Sage Intacct connection." placement="left" />
                  </Button>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Configuration</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={configuration.status} message={configuration.message} />
                </Table.Cell>
                <Table.Cell>
                  {configuration.status !== 'loading' &&
                    (configuration.data?.code === 'ready' ? (
                      'Ready'
                    ) : (
                      <Errors>
                        {configuration.data?.errors?.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </Errors>
                    ))}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditConfiguration} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditConfiguration()}>
                          Edit Configuration
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupConfiguration()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Webhook</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={company.status} message={company.message} />
                </Table.Cell>
                <Table.Cell>Trigger Ruddr updates when records change in Sage Intacct.</Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.View disabled={disabled} onClick={handleEditWebhook} />
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Clients</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={clients.status} message={clients.message} />
                </Table.Cell>
                <Table.Cell>
                  {clients.data && (
                    <>
                      {clients.data.filter((c) => !!c.sageCustomerId).length} of {clients.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditClients} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditClients()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupClients()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Invoice Items</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={invoiceItems.status} message={invoiceItems.message} />
                </Table.Cell>
                <Table.Cell>
                  {invoiceItems.data && (
                    <>
                      {
                        invoiceItems.data.filter((i) =>
                          configuration.syncMethod === 'documents' ? !!i.sageItemKey : !!i.sageIncomeAccountId,
                        ).length
                      }{' '}
                      of {invoiceItems.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditInvoiceItems} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditInvoiceItems()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupInvoiceItems()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Tax Rates</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={taxRates.status} message={taxRates.message} />
                </Table.Cell>
                <Table.Cell>
                  {taxRates.data && (
                    <>
                      {taxRates.data.filter((t) => !!t.sageTaxDetailId).length} of {taxRates.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditTaxRates} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditTaxRates()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupTaxRates()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Deposit Accounts</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={depositAccounts.status} message={depositAccounts.message} />
                </Table.Cell>
                <Table.Cell>
                  {depositAccounts.data && (
                    <>
                      {depositAccounts.data.filter((d) => !!d.sageBankAccountId || !!d.sageDirectAccountId).length} of{' '}
                      {depositAccounts.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditDepositAccounts} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditDepositAccounts()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupDepositAccounts()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Payment Methods</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={paymentMethods.status} message={paymentMethods.message} />
                </Table.Cell>
                <Table.Cell>
                  {paymentMethods.data && (
                    <>
                      {paymentMethods.data.filter((p) => !!p.sagePaymentMethodValue).length} of{' '}
                      {paymentMethods.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditPaymentMethods} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditPaymentMethods()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupPaymentMethods()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Members</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={memberMappings.status} message={memberMappings.message} />
                </Table.Cell>
                <Table.Cell>
                  {memberMappings.data && (
                    <>
                      {memberMappings.data.filter((m) => !!m.sageVendorId).length} of {memberMappings.data.length}{' '}
                      connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditMemberMappings} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditMemberMappings()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupMemberMappings()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>

              <Table.BoxRow>
                <Table.Cell>
                  <strong>Expense Categories</strong>
                </Table.Cell>
                <Table.Cell>
                  <Status status={expenseCategoryMappings.status} message={expenseCategoryMappings.message} />
                </Table.Cell>
                <Table.Cell>
                  {expenseCategoryMappings.data && (
                    <>
                      {
                        expenseCategoryMappings.data.filter((c) =>
                          configuration.syncMethod === 'documents' ? !!c.sageItemKey : !!c.sageAccountId,
                        ).length
                      }{' '}
                      of {expenseCategoryMappings.data.length} connected
                    </>
                  )}
                </Table.Cell>
                <TableBoxRowActions>
                  <TableBoxRowActions.Edit disabled={disabled} onClick={handleEditExpenseCategoryMappings} />
                  <hr />
                  <TableBoxRowActions.Dropdown disabled={disabled}>
                    {({ setIsOpen }) => (
                      <>
                        <Dropdown.Item onClick={() => setIsOpen(false) || handleEditExpenseCategoryMappings()}>
                          Edit Mappings
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setIsOpen(false) || setupExpenseCategoryMappings()}>
                          Load from Sage Intacct
                        </Dropdown.Item>
                      </>
                    )}
                  </TableBoxRowActions.Dropdown>
                </TableBoxRowActions>
              </Table.BoxRow>
            </Table.Body>
          </Table>
        </Page.Section>
      )}

      {tabIndex === 1 && (
        <Page.Section>
          <SyncResultsBlock>
            <SyncResults refreshDate={forceRefreshSyncsDate} onSyncNow={handleSyncNow} />
          </SyncResultsBlock>
        </Page.Section>
      )}

      {dialog &&
        {
          configuration: () => <ConfigurationDrawer onClose={handleCloseConfiguration} />,
          clients: () => <ClientMappings onClose={handleCloseClients} />,
          invoice_items: () => <InvoiceItemMappings onClose={handleCloseInvoiceItems} />,
          tax_rates: () => <TaxRateMappings onClose={handleCloseTaxRates} />,
          payment_methods: () => <PaymentMethodMappings onClose={handleClosePaymentMethods} />,
          deposit_accounts: () => <DepositAccountMappings onClose={handleCloseDepositAccounts} />,
          member_mappings: () => <MemberMappings onClose={handleCloseMemberMappings} />,
          expense_category_mappings: () => <ExpenseCategoryMappings onClose={handleCloseExpenseCategoryMappings} />,
          webhook: () => <WebhookDrawer onClose={handleCloseWebhook} />,
        }[dialog]()}
    </>
  );
}
